import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Bell, MessageCircle, Settings, LogOut, Globe, Shield, Home } from 'lucide-react';
import { BrandLogo } from './BrandLogo';
import { FacebookIcon } from './FacebookIcon';
import { InstagramIcon } from './InstagramIcon';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TopNav() {
  const navigate = useNavigate();
  const location = useLocation();
  // While the user is INSIDE the Messages app, mute both nav badges: the message
  // icon (they already see per-tab + per-conversation badges) and the bell
  // (message-derived notifications duplicate the same signal). Badges resurface
  // the moment they navigate elsewhere.
  const onMessagesRoute = location.pathname.startsWith('/messages');
  const { user, getAuthHeaders, logout } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifCount, setNotifCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const { lang, setLang, languages } = useLanguage();
  const notifRef = useRef(null);
  const langRef = useRef(null);
  const [avatarError, setAvatarError] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches);

  const fetchCounts = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [unread, notif] = await Promise.all([
        axios.get(`${API}/notifications/unread-count`, { headers }),
        axios.get(`${API}/notifications`, { headers }),
      ]);
      setUnreadCount(unread.data.messages || 0);
      const notifs = notif.data || [];
      setNotifications(notifs);
      const unreadNotifs = notifs.filter(n => !n.read).length;
      setNotifCount(unreadNotifs);
      // Update PWA app badge
      const totalBadge = (unread.data.messages || 0) + unreadNotifs;
      if ('setAppBadge' in navigator) {
        totalBadge > 0 ? navigator.setAppBadge(totalBadge) : navigator.clearAppBadge();
      }
    } catch (_e) { /* noop */ }
  }, [getAuthHeaders]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, [user, fetchCounts]);

  // Refresh counts when app returns from background (critical for iOS PWA and all mobile)
  useEffect(() => {
    if (!user) return;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') fetchCounts();
    };
    const onFocus = () => fetchCounts();
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
    };
  }, [user, fetchCounts]);

  // Listen for message-read events from Messaging page
  useEffect(() => {
    const handler = () => fetchCounts();
    window.addEventListener('messages-read', handler);
    return () => window.removeEventListener('messages-read', handler);
  }, [fetchCounts]);

  // Listen for push notification events from the service worker
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const swHandler = (event) => {
      if (event.data?.type === 'PUSH_RECEIVED') {
        fetchCounts();
      }
    };
    navigator.serviceWorker.addEventListener('message', swHandler);
    return () => navigator.serviceWorker.removeEventListener('message', swHandler);
  }, [fetchCounts]);

  useEffect(() => {
    const handleClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifs(false);
      if (langRef.current && !langRef.current.contains(e.target)) setShowLangMenu(false);
    };
    if (showNotifs || showLangMenu) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showNotifs, showLangMenu]);


  const openNotifications = async () => {
    setShowNotifs(!showNotifs);
    if (!showNotifs) {
      try {
        const headers = getAuthHeaders();
        const r = await axios.get(`${API}/notifications`, { headers });
        setNotifications(r.data);
        await axios.post(`${API}/notifications/mark-read`, {}, { headers });
        setNotifCount(0);
      } catch (_e) { /* noop */ }
    }
  };

  const displayName = user?.user_type === 'provider'
    ? (user?.daycare_name || user?.name)
    : user?.name;

  if (!user) return null;

  const siteName = user?.care_segment === 'eldercare' ? 'ElderCareConnections' : 'ChildCareConnections';
  const logoBlock = (
    <div className="flex items-center gap-2 cursor-pointer min-w-0" onClick={() => navigate('/dashboard')}>
      <BrandLogo size={36} className="rounded-xl shrink-0" />
      <span className="text-base sm:text-xl font-semibold truncate" style={{ fontFamily: 'Fredoka' }}>{siteName}</span>
    </div>
  );
  const facebookBtn = (
    <a href="https://www.facebook.com/profile.php?id=61575771127335" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} data-testid="facebook-link" aria-label="Visit ChildCareConnections on Facebook" title="Follow us on Facebook" className="flex items-center justify-center w-9 h-9 rounded-full text-[#1877F2] hover:bg-[#1877F2]/10 transition-colors shrink-0">
      <FacebookIcon className="w-5 h-5" />
    </a>
  );
  const instagramBtn = (
    <a href="https://www.instagram.com/childcareconnections.ca" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} data-testid="instagram-link" aria-label="Visit ChildCareConnections on Instagram" title="Follow us on Instagram" className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-[#d6249f]/10 transition-colors shrink-0">
      <InstagramIcon className="w-5 h-5" />
    </a>
  );
  const langSelector = (
    <div className="relative" ref={langRef}>
      <button data-testid="lang-selector" onClick={() => setShowLangMenu(!showLangMenu)} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl hover:bg-white/40 transition-colors text-slate-500">
        <Globe className="w-4 h-4" />
        <span className="text-xs font-medium hidden sm:block">{languages.find(l => l.code === lang)?.label || 'English'}</span>
      </button>
      {showLangMenu && (
        <div className="absolute right-0 top-full mt-1 w-44 max-w-[calc(100vw-2rem)] bg-white/95 backdrop-blur-xl border border-white/50 rounded-2xl shadow-2xl py-1 overflow-hidden" style={{ zIndex: 9999 }}>
          {languages.map(l => (
            <button key={l.code} data-testid={`lang-${l.code}`} onClick={() => { setLang(l.code); setShowLangMenu(false); }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${lang === l.code ? 'bg-violet-50 text-violet-700 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}>
              <span className="text-base">{l.flag}</span>
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
  const homeBtn = (
    <button data-testid="nav-home-btn" onClick={() => navigate('/dashboard')} title="Home" aria-label="Go to dashboard" className="p-2 rounded-xl hover:bg-white/40 transition-colors text-slate-600">
      <Home className="w-5 h-5" />
    </button>
  );
  const adminBtn = user?.user_type === 'admin' ? (
    <button data-testid="nav-admin-btn" onClick={() => navigate('/admin')} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl hover:bg-white/40 transition-colors text-violet-500">
      <Shield className="w-4 h-4" />
      <span className="text-xs font-medium hidden sm:block">Admin</span>
    </button>
  ) : null;
  const notifBtn = (
    <div className="relative" ref={notifRef}>
      <button data-testid="nav-notifications-btn" onClick={openNotifications} className="relative p-2 rounded-xl hover:bg-white/40 transition-colors">
        <Bell className="w-5 h-5 text-slate-600" />
        {notifCount > 0 && !onMessagesRoute && <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">{notifCount}</span>}
      </button>
      {showNotifs && (
        <div className={`${isMobile ? 'fixed left-3 right-3 top-28 w-auto' : 'absolute right-0 top-full mt-2 w-80'} max-w-[calc(100vw-1.5rem)] bg-white/90 backdrop-blur-2xl border border-white/50 rounded-2xl shadow-2xl max-h-80 overflow-y-auto`} style={{ zIndex: 9999 }} data-testid="notifications-dropdown">
          <div className="p-3 border-b border-white/20"><p className="font-semibold text-slate-700 text-sm" style={{ fontFamily: 'Fredoka' }}>Notifications</p></div>
          {notifications.length === 0 && <p className="p-4 text-center text-slate-400 text-sm">No notifications</p>}
          {notifications.slice(0, 10).map((n, i) => (
            <div key={n.notification_id || i} onClick={() => { if (n.link) navigate(n.link); setShowNotifs(false); }}
              className={`p-3 hover:bg-white/30 cursor-pointer border-b border-white/10 ${!n.read ? 'bg-violet-50/50' : ''}`}>
              <p className="text-sm font-medium text-slate-700">{n.title}</p>
              <p className="text-xs text-slate-500">{n.message}</p>
              <p className="text-xs text-slate-300 mt-1">{new Date(n.created_at).toLocaleDateString()}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
  const messagesBtn = (
    <button data-testid="nav-messages-btn" onClick={() => navigate('/messages')} className="relative p-2 rounded-xl hover:bg-white/40 transition-colors">
      <MessageCircle className="w-5 h-5 text-slate-600" />
      {unreadCount > 0 && !onMessagesRoute && <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-rose-500 text-white text-xs flex items-center justify-center">{unreadCount}</span>}
    </button>
  );
  const settingsBtn = (
    <button data-testid="nav-settings-btn" onClick={() => navigate('/settings')} className="p-2 rounded-xl hover:bg-white/40 transition-colors">
      <Settings className="w-5 h-5 text-slate-500" />
    </button>
  );
  const profileBtn = (
    <div data-testid="nav-profile-btn" onClick={() => navigate(`/profile/${user?.user_id}`)}
      className="flex items-center gap-2 glass-panel rounded-full px-3 py-1.5 cursor-pointer hover:bg-white/70 transition-all shrink-0 min-w-0 max-w-[48%] sm:max-w-none">
      {user?.picture && !avatarError ? (
        <img src={user.picture} alt="" onError={() => setAvatarError(true)} className="w-7 h-7 rounded-full object-cover shrink-0" />
      ) : (
        <div className="w-7 h-7 rounded-full bg-violet-200 flex items-center justify-center text-violet-600 text-xs font-medium shrink-0">{user?.name?.[0] || 'U'}</div>
      )}
      <span className="text-sm font-medium text-slate-700 truncate">{displayName}</span>
    </div>
  );
  const logoutBtn = (
    <button data-testid="nav-logout-btn" onClick={() => { logout(); navigate('/'); }} className="p-2 rounded-xl hover:bg-white/40 transition-colors">
      <LogOut className="w-5 h-5 text-slate-500" />
    </button>
  );

  return (
    <nav className="relative px-4 md:px-8 py-4 shrink-0" style={{ zIndex: 9999 }}>
      <div className="max-w-7xl mx-auto">
        {isMobile ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
              {logoBlock}
              {profileBtn}
            </div>
            <div className="flex items-center justify-between gap-1">
              <span className="flex items-center gap-0.5">{facebookBtn}{instagramBtn}</span>
              <div className="flex items-center gap-0.5">
                {langSelector}
                {homeBtn}
                {adminBtn}
                {notifBtn}
                {messagesBtn}
                {settingsBtn}
                {logoutBtn}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {logoBlock}
              <span className="flex items-center gap-0.5">{facebookBtn}{instagramBtn}</span>
            </div>
            <div className="flex items-center gap-3">
              {langSelector}
              {homeBtn}
              {adminBtn}
              {notifBtn}
              {messagesBtn}
              {settingsBtn}
              {profileBtn}
              {logoutBtn}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
